import { type FieldValues, useController } from 'react-hook-form';

import type { ComponentProps } from '~/chords/ComponentProps';
import { FormField, type FormFieldProps } from '~/chords/FormField';
import { Dropdown, type DropdownProps } from '~/components/Dropdown';

type FormFieldDropdownProps<
  TValue extends string,
  TFormFields extends FieldValues,
  TMultiselect extends boolean | undefined,
> = ComponentProps<
  Omit<FormFieldProps<TFormFields>, 'children'> &
    Omit<DropdownProps<TValue, TMultiselect>, 'name' | 'className'> & {
      onChange?: (value: string | string[]) => void;
    }
>;

export function FormFieldDropdown<
  TValue extends string,
  TFormFields extends FieldValues,
  TMultiselect extends boolean | undefined,
>({
  name,
  label,
  dangerouslyOverrideClassName,
  disabled = false,
  readOnly = false,
  triggerClassName,
  labelAddon,
  ...dropdownProps
}: FormFieldDropdownProps<TValue, TFormFields, TMultiselect>) {
  const {
    field,
    fieldState: { invalid },
  } = useController({ name, disabled });

  return (
    <FormField
      name={name}
      label={label}
      dangerouslyOverrideClassName={dangerouslyOverrideClassName}
      disabled={disabled}
      readOnly={readOnly}
      labelAddon={labelAddon}
      labelAsSpan
    >
      <Dropdown
        {...field}
        label={label}
        state={invalid ? 'error' : undefined}
        {...dropdownProps}
        triggerClassName={triggerClassName}
        disabled={disabled || readOnly}
        aria-label={name}
        onChange={(value) => {
          field.onChange(value);
          dropdownProps.onChange?.(value);
        }}
      />
    </FormField>
  );
}

FormFieldDropdown.displayName = 'FormFieldDropdown';
